import React, { useEffect, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import type { MessageStudentsProps as MessageStudentsType } from '../modules/MessageStudents';
import type { StudentType, YourStudentsRecentStateType } from '../../types';
import type { AllColorType } from '../../../util/types';
import { CollapseSection, StudentsScatterDisplay } from '../modules';
import { CongratulateButton, InfoButton, StudentsTable } from '../../../global';
import { Alert, Button, Collapse, Loader } from '../../../components';
import {
  mapGroupReportForLastMonday,
} from '../../../mapper/groupMapper';
import { getPolishedColors } from '../../../util/styled';
import styled from 'styled-components';
import type { SectionProps } from '../Home';

interface Props extends SectionProps {
  dateSelector: React.ReactNode;
  /**
   * Handles click on "Message them" button
   * in order to show message modal.
   */
  onMessageStudents(students: MessageStudentsType['students'], type: MessageStudentsType['type']): void;
  shake?: boolean;
}

type GetStudentByProgressionsProps = {
  data: StudentType[],
  min: number,
  max?: number,
  includeNoLogin?: boolean,
};

const RecentData = ({ isLoading, groupReport, onMessageStudents, dateSelector, open, setOpen, shake }: Props): JSX.Element => {
  const { translate } = useI18n();

  const [ dataLastMonday, setDataLastMonday ] = useState<StudentType[]>([]);
  const [ showInfo, setShowInfo ] = useState<boolean>(false);
  const [ showFullDetails, setShowFullDetails ] = useState<boolean>(false);

  useEffect(() => {
    if (null == groupReport) {
      return;
    }

    setDataLastMonday(mapGroupReportForLastMonday(groupReport));
  }, [ groupReport ]);

  const NoStudentsReturned = () => <div className={'text-muted italic'}>{translate('pages.HOME.LAST_WEEKS.NO_RECORDS_RETURNED')}</div>;

  const getFilteredByState = (data: StudentType[], state: YourStudentsRecentStateType) => {
    return data.filter((student) => student.yourStudentsStates && student.yourStudentsStates.includes(state));
  };

  const getStudentsByProgressions = ({ data, min, max, includeNoLogin = true }: GetStudentByProgressionsProps) => {
    return data.filter((student) => {
      if (!includeNoLogin) {
        const studentNotLoggingIn = student.yourStudentsStates && student.yourStudentsStates.includes('no_logging');
        if (studentNotLoggingIn) {
          return false;
        }
      }
      if (student.chartsData) {
        const matchMin = student.chartsData?.progression >= min;
        const matchMax = student.chartsData?.progression <= (max ?? Infinity);
        return (matchMin && matchMax);
      }
    });
  };

  const progressions3 = getStudentsByProgressions({ data: dataLastMonday, min: 3 });
  const progressions1_2 = getStudentsByProgressions({ data: dataLastMonday, min: 1, max: 2 });
  const progressions0 = getStudentsByProgressions({ data: dataLastMonday, min: 0, max: 0, includeNoLogin: false });
  const studentsNotLoggedIn = getFilteredByState(dataLastMonday, 'no_logging');

  const numberOfStudentsWithLessThan3Progressions = dataLastMonday.length - progressions3.length;

  return (
    <CollapseSection
      headline={isLoading ? '' : translate('pages.HOME.RECENT.HEADLINE')}
      strapline={isLoading ? '' : translate('pages.HOME.RECENT.STRAPLINE')}
      dataCy={'home_recent'}
      open={open}
      setOpen={setOpen}
      shake={shake}
    >
      {isLoading
        ? (<Loader />)
        : (<>
          <div data-cy="recent-container">
            <div className={'mb-md'}>{dateSelector}</div>
            {dataLastMonday.length === 0
              ? (<NoStudentsReturned />)
              : (
                <div>
                  <div className={'space-y-2'}>
                    <StyledWrapper className={'metrics grid gap-2 grid-cols-auto-1fr items-center p-sm rounded-default'} $color={'header'}>
                      <div className={'flex items-center justify-center rounded-default p-1 bg-header text-2xl md:text-5xl font-bold'}>{progressions3.length}</div>
                      <div className={'flex items-center text-md md:text-2xl font-bold'}>
                        {translate('pages.HOME.YOUR_STUDENTS.GREAT_JOB.DESCRIPTION', { count: progressions3.length })}
                        <InfoButton onClick={() => setShowInfo(!showInfo)} />
                      </div>
                      <div>
                        <div className={'flex items-center justify-center rounded-default bg-white p-1 text-2xl md:text-5xl font-bold'}>{numberOfStudentsWithLessThan3Progressions}</div>
                      </div>
                      <div className={'flex items-center text-md md:text-2xl font-bold'}>
                        {translate('pages.HOME.YOUR_STUDENTS.NOT_DONE_WEEKLY_PROGRESSIONS.DESCRIPTION', { count: numberOfStudentsWithLessThan3Progressions })}
                      </div>
                    </StyledWrapper>
                    <Alert disableIcon show={showInfo} className={'my-md'}>
                      {translate('pages.HOME.YOUR_STUDENTS.GREAT_JOB.INFO')}
                    </Alert>
                  </div>
                  <div className={'space-y-8'}>
                    <StudentsScatterDisplay
                      chart={'progression'}
                      period={'recent'}
                      data={dataLastMonday}
                      className={'pb-lg border-b'}
                      data-cy="recent-progression"
                    />
                    <StudentsScatterDisplay
                      chart={'qualityLearningTime'}
                      period={'recent'}
                      data={dataLastMonday}
                      className={'pb-lg'}
                      data-cy="recent-learning"
                    />
                  </div>
                  <div className={'mb-md'}>
                    <div className='flex justify-center'>
                      <Button
                        isOutline
                        color={'positive'}
                        icon={'ViewList'}
                        toggle={showFullDetails}
                        isBold
                        size={'lg'}
                        onClick={() => setShowFullDetails((prev) => !prev)}
                      >{translate('components.BUTTONS.FULL_DETAILS')}</Button>
                    </div>
                    <Collapse when={showFullDetails}>
                      <div className={'container mt-md border-2 border-positive rounded-md space-y-12'}>
                        { !!progressions3.length && (
                          <section>
                            <div className={'flex justify-between items-center mb-md font-bold uppercase'}>
                              {translate('pages.HOME.RECENT.PROGRESSIONS.3')}
                              <CongratulateButton onClick={() => onMessageStudents(progressions3, 'progressions3')} />
                            </div>
                            <StudentsTable
                              id={'progressions3'}
                              data={progressions3}
                              column={[ 'progressions', 'minutes' ]}
                              color={'positive'}
                            />
                          </section>
                        )}
                        { !!progressions1_2.length && (
                          <section>
                            <div className={'flex justify-between items-center mb-md font-bold uppercase'}>
                              {translate('pages.HOME.RECENT.PROGRESSIONS.1-2')}
                              <CongratulateButton onClick={() => onMessageStudents(progressions1_2, 'progressions1-2')} />
                            </div>
                            <StudentsTable
                              id={'progressions1_2'}
                              data={progressions1_2}
                              column={[ 'progressions', 'minutes' ]}
                              color={'neutral'}
                            />
                          </section>
                        )}
                        { !!progressions0.length && (
                          <section>
                            <div className={'flex justify-between items-center mb-md font-bold uppercase'}>
                              {translate('pages.HOME.RECENT.PROGRESSIONS.0')}
                              <CongratulateButton onClick={() => onMessageStudents(progressions0, 'progressions0')} />
                            </div>
                            <StudentsTable
                              id={'progressions0'}
                              data={progressions0}
                              column={[ 'minutes' ]}
                              color={'negative'}
                            />
                          </section>
                        )}
                        { !!studentsNotLoggedIn.length && (
                          <section>
                            <div className={'font-bold uppercase'}>{translate('pages.HOME.RECENT.NOT_LOGGED_IN.LABEL')}</div>
                            <StudentsTable
                              id={''}
                              data={studentsNotLoggedIn}
                              column={[ 'daysSinceLastLogon', 'login_details' ]}
                              color={'negative'}
                              sortBy={{
                                column: 'daysSinceLastLogon',
                                desc: true,
                              }}
                            />
                          </section>
                        )}
                      </div>
                    </Collapse>
                  </div>
                </div>
              )
            }
          </div>
        </>)
      }
    </CollapseSection>
  );
};

const StyledWrapper = styled('div')<{ $color: AllColorType }>`
  background: ${(props) => getPolishedColors(props).light.color};
`;

RecentData.displayName = 'RecentData';

export default RecentData;
